import React, { useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  Paper,
  Button,
  TextField,
  IconButton,
  Drawer,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  InputAdornment,
  Tooltip
} from "@material-ui/core";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  VideoLibraryOutlined as VideoIcon,
  SaveOutlined as SaveIcon,
  DeleteOutline as DeleteIcon
} from "@material-ui/icons";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";

const BLUE = "#1348ee";
const BLANK_RECORD = { title: "", description: "", video: "" };

// Os registros existentes usam o ID do YouTube; URLs são interpretadas apenas para exibição.
// O valor original continua sendo enviado à mesma API, sem mudar a regra de armazenamento.
const getYoutubeId = (input) => {
  const raw = String(input || "").trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;
  const match = raw.match(/(?:youtube\.com\/(?:watch\?[^#]*?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", background: "transparent", color: theme.palette.text.primary },
  surface: {
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    borderRadius: 14,
    boxShadow: theme.palette.type === "dark" ? "none" : "0 7px 24px rgba(26,48,96,.035)"
  },
  shell: { padding: "20px 19px", boxSizing: "border-box" },
  heading: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 19 },
  title: { fontWeight: 750, fontSize: 21, lineHeight: 1.25, margin: 0 },
  subtitle: { fontSize: 13, color: theme.palette.text.secondary, margin: "5px 0 0" },
  toolbar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 },
  search: { flex: "1 1 255px", minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: 9, background: theme.palette.background.paper } },
  primary: {
    textTransform: "none", background: BLUE, color: "white", fontWeight: 700, borderRadius: 9,
    minHeight: 41, padding: "0 16px", boxShadow: "0 4px 12px rgba(19,72,238,.15)", whiteSpace: "nowrap",
    "&:hover": { background: "#0a37c3" }, "&.Mui-disabled": { color: "#fff", opacity: .55 }
  },
  cards: {
    display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 15,
    "@media (max-width: 1070px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    "@media (max-width: 680px)": { gridTemplateColumns: "1fr" }
  },
  card: {
    overflow: "hidden", minWidth: 0, display: "flex", flexDirection: "column", minHeight: 321,
    border: `1px solid ${theme.palette.divider}`, borderRadius: 12, background: theme.palette.background.paper,
    transition: "border-color .16s, box-shadow .16s", "&:hover": { borderColor: "#b2c6fa", boxShadow: "0 8px 24px rgba(31,59,116,.08)" }
  },
  thumbnail: { position: "relative", width: "100%", aspectRatio: "16 / 9", background: theme.palette.type === "dark" ? theme.palette.background.default : "#edf2fb", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center" },
  thumbnailImage: { display: "block", width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 },
  playOverlay: {
    position: "relative", zIndex: 1, width: 46, height: 46, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", background: "rgba(12,22,42,.78)", boxShadow: "0 2px 14px rgba(0,0,0,.2)",
    "&:hover": { background: BLUE }
  },
  cardContent: { flex: 1, minHeight: 0, padding: "14px 15px 16px", display: "flex", flexDirection: "column" },
  cardTitle: { fontSize: 15, fontWeight: 720, lineHeight: 1.35, overflowWrap: "anywhere", margin: "0 0 7px" },
  cardDescription: {
    fontSize: 12, lineHeight: 1.45, color: theme.palette.text.secondary, margin: 0, overflowWrap: "anywhere",
    overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 35
  },
  actions: { display: "flex", marginTop: "auto", gap: 9, paddingTop: 15 },
  watchButton: { flex: 1, borderColor: "#afc4ff", color: BLUE, borderRadius: 8, textTransform: "none", fontWeight: 700, "&:hover": { borderColor: BLUE, background: "rgba(19,72,238,.045)" } },
  editButton: { flex: 1, borderColor: theme.palette.divider, color: theme.palette.text.primary, borderRadius: 8, textTransform: "none", fontWeight: 600 },
  newCard: {
    minHeight: 321, cursor: "pointer", border: `1.5px dashed ${theme.palette.divider}`, borderRadius: 12,
    background: theme.palette.type === "dark" ? theme.palette.background.paper : "#fbfcff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 8, padding: 22, boxSizing: "border-box",
    "&:hover": { borderColor: BLUE, background: theme.palette.action.hover }
  },
  newIcon: { width: 55, height: 55, color: BLUE, background: "#eaf1ff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 5 },
  emptyText: { gridColumn: "1/-1", padding: 28, textAlign: "center", color: theme.palette.text.secondary },
  drawerPaper: { width: 450, maxWidth: "100vw", background: theme.palette.background.paper, boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", padding: "20px 20px 17px", borderBottom: `1px solid ${theme.palette.divider}` },
  drawerTitle: { fontSize: 19, fontWeight: 750, margin: "0 0 4px" },
  drawerDescription: { fontSize: 12, color: theme.palette.text.secondary, margin: 0 },
  drawerBody: { overflowY: "auto", flex: 1, padding: "18px 20px" },
  section: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 16, marginBottom: 17 },
  sectionLabel: { display: "flex", alignItems: "center", gap: 9, fontSize: 14, fontWeight: 720, marginBottom: 15 },
  field: { width: "100%", marginBottom: 15, "& .MuiOutlinedInput-root": { borderRadius: 8 } },
  preview: { width: "100%", aspectRatio: "16 / 9", background: theme.palette.type === "dark" ? theme.palette.background.default : "#edf1f7", borderRadius: 10, overflow: "hidden", position: "relative", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 7, color: theme.palette.text.secondary, fontSize: 12, textAlign: "center" },
  previewImage: { width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 },
  previewPlay: { zIndex: 1, color: "white", background: "rgba(12,22,42,.75)", borderRadius: "50%", height: 48, width: 48, display: "flex", alignItems: "center", justifyContent: "center" },
  footer: { borderTop: `1px solid ${theme.palette.divider}`, padding: 16, display: "flex", gap: 9, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", background: theme.palette.background.paper },
  secondary: { textTransform: "none", borderRadius: 8, fontWeight: 600 },
  deleteButton: { textTransform: "none", marginRight: "auto", color: "#d02a42" },
  videoDialog: { width: "min(960px, 95vw)", maxWidth: "none", borderRadius: 12 },
  player: { position: "relative", width: "100%", paddingTop: "56.25%", background: "#0a1020" },
  iframe: { position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }
}));

// Mantém a API pública do componente de formulário e os mesmos nomes de campos.
export function HelpManagerForm({ onSubmit, onDelete, onCancel, initialValue, loading }) {
  const classes = useStyles();
  return (
    <Formik enableReinitialize initialValues={initialValue || BLANK_RECORD} onSubmit={onSubmit}>
      {({ values, handleChange }) => {
        const youtubeId = getYoutubeId(values.video);
        return (
          <Form style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
            <div className={classes.drawerBody}>
              <div className={classes.section}>
                <div className={classes.sectionLabel}><VideoIcon fontSize="small" color="primary" /> Dados do tutorial</div>
                <TextField className={classes.field} variant="outlined" size="small" label="Título" name="title" value={values.title || ""} onChange={handleChange} required />
                <TextField className={classes.field} variant="outlined" size="small" label={i18n.t("helps.settings.codeVideo")} name="video" value={values.video || ""} onChange={handleChange} helperText="Informe o código do vídeo do YouTube." />
                <TextField className={classes.field} variant="outlined" size="small" label={i18n.t("helps.settings.description")} name="description" value={values.description || ""} onChange={handleChange} multiline rows={4} />
              </div>
              <div className={classes.section}>
                <div className={classes.sectionLabel}><PlayIcon fontSize="small" color="primary" /> Prévia do vídeo</div>
                <div className={classes.preview}>
                  {youtubeId ? (
                    <>
                      <img className={classes.previewImage} src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} alt="Prévia do tutorial" />
                      <span className={classes.previewPlay}><PlayIcon /></span>
                    </>
                  ) : (
                    <><VideoIcon fontSize="large" /><span>A prévia aparecerá aqui<br />após informar o código do vídeo.</span></>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.footer}>
              {initialValue && initialValue.id !== undefined ? (
                <Button disabled={loading} onClick={onDelete} startIcon={<DeleteIcon />} className={classes.deleteButton}>Excluir</Button>
              ) : null}
              <Button disabled={loading} onClick={onCancel} className={classes.secondary} variant="outlined">Cancelar</Button>
              <Button disabled={loading} type="submit" className={classes.primary} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>Salvar vídeo</Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

// A antiga grade de tabela é apresentada como cartões; a seleção continua chamando onSelect.
export function HelpsManagerGrid({ records, onSelect, onWatch, onNew }) {
  const classes = useStyles();
  return (
    <div className={classes.cards}>
      {records.map((row) => {
        const youtubeId = getYoutubeId(row.video);
        return (
          <Paper key={row.id} elevation={0} className={classes.card}>
            <div className={classes.thumbnail}>
              {youtubeId ? (
                <img className={classes.thumbnailImage} src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} alt={`Miniatura de ${row.title || "vídeo"}`} loading="lazy" />
              ) : <VideoIcon fontSize="large" color="disabled" />}
              <Tooltip title={youtubeId ? "Assistir tutorial" : "Código de vídeo inválido ou ausente"}>
                <span>
                  <IconButton disabled={!youtubeId} className={classes.playOverlay} aria-label={`Assistir ${row.title || "tutorial"}`} onClick={() => onWatch(row)}><PlayIcon /></IconButton>
                </span>
              </Tooltip>
            </div>
            <div className={classes.cardContent}>
              <div className={classes.cardTitle}>{row.title || "Sem título"}</div>
              <p className={classes.cardDescription}>{row.description || "Vídeo de orientação do sistema."}</p>
              <div className={classes.actions}>
                <Button disabled={!youtubeId} variant="outlined" className={classes.watchButton} startIcon={<PlayIcon />} onClick={() => onWatch(row)}>Assistir</Button>
                <Button variant="outlined" className={classes.editButton} startIcon={<EditIcon />} onClick={() => onSelect(row)}>Editar</Button>
              </div>
            </div>
          </Paper>
        );
      })}
      {!records.length && <div className={classes.emptyText}>Nenhum tutorial encontrado para esta pesquisa.</div>}
      <button type="button" className={classes.newCard} onClick={onNew}>
        <span className={classes.newIcon}><AddIcon fontSize="large" /></span>
        <strong style={{ fontSize: 15, color: "inherit" }}>Adicionar tutorial</strong>
        <span style={{ fontSize: 12 }}>Cadastre um novo vídeo de ajuda.</span>
        <span style={{ fontSize: 12, color: BLUE, marginTop: 3, fontWeight: 700 }}>+ Novo vídeo</span>
      </button>
    </div>
  );
}

export default function HelpsManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useHelps();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(BLANK_RECORD);
  const [editorOpen, setEditorOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [watchVideo, setWatchVideo] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    let active = true;
    list().then((helpList) => { if (active) setRecords(Array.isArray(helpList) ? helpList : []); })
      .catch(() => { if (active) toast.error("Não foi possível carregar a lista de registros"); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(() => {
    const text = search.trim().toLocaleLowerCase("pt-BR");
    if (!text) return records;
    return records.filter((item) => `${item.title || ""} ${item.description || ""}`.toLocaleLowerCase("pt-BR").includes(text));
  }, [records, search]);

  const loadHelps = async () => {
    const helpList = await list();
    setRecords(Array.isArray(helpList) ? helpList : []);
  };

  const closeEditor = () => {
    if (loading) return;
    setEditorOpen(false);
    setRecord({ ...BLANK_RECORD });
  };
  const handleNew = () => { setRecord({ ...BLANK_RECORD }); setEditorOpen(true); };
  const handleSelect = (data) => {
    setRecord({ id: data.id, title: data.title || "", description: data.description || "", video: data.video || "" });
    setEditorOpen(true);
  };
  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) await update(data);
      else await save(data);
      await loadHelps();
      setEditorOpen(false);
      setRecord({ ...BLANK_RECORD });
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação. Verifique se já existe um tutorial com o mesmo nome ou se os campos foram preenchidos corretamente");
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    if (record.id === undefined) return;
    setLoading(true);
    try {
      await remove(record.id);
      await loadHelps();
      setShowConfirmDialog(false);
      setEditorOpen(false);
      setRecord({ ...BLANK_RECORD });
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    } finally { setLoading(false); }
  };
  const currentId = watchVideo ? getYoutubeId(watchVideo.video) : null;

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={`${classes.surface} ${classes.shell}`}>
        <div className={classes.heading}>
          <div><h2 className={classes.title}>Ajuda e Tutoriais</h2><p className={classes.subtitle}>Gerencie os vídeos de orientação do sistema.</p></div>
        </div>
        <div className={classes.toolbar}>
          <TextField value={search} onChange={(event) => setSearch(event.target.value)} variant="outlined" size="small" placeholder="Buscar tutorial por título ou descrição..." className={classes.search} inputProps={{ "aria-label": "Pesquisar tutoriais" }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
          <Button className={classes.primary} onClick={handleNew} startIcon={<AddIcon />}>Novo vídeo</Button>
        </div>
        <HelpsManagerGrid records={filteredRecords} onSelect={handleSelect} onWatch={setWatchVideo} onNew={handleNew} />
      </Paper>
      <Drawer anchor="right" open={editorOpen} onClose={closeEditor} classes={{ paper: classes.drawerPaper }}>
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <div className={classes.drawerHeader}>
          <div><h3 className={classes.drawerTitle}>{record.id === undefined ? "Adicionar tutorial" : "Editar tutorial"}</h3><p className={classes.drawerDescription}>Preencha os dados para {record.id === undefined ? "cadastrar" : "atualizar"} o vídeo de ajuda.</p></div>
          <IconButton aria-label="Fechar formulário" onClick={closeEditor} disabled={loading}><CloseIcon /></IconButton>
        </div>
        <HelpManagerForm initialValue={record} onSubmit={handleSubmit} onDelete={() => setShowConfirmDialog(true)} onCancel={closeEditor} loading={loading} />
        </div>
      </Drawer>
      <Dialog open={Boolean(currentId)} onClose={() => setWatchVideo(null)} maxWidth={false} classes={{ paper: classes.videoDialog }} aria-labelledby="help-video-dialog-title">
        <DialogTitle id="help-video-dialog-title" disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <strong style={{ fontSize: 16, overflowWrap: "anywhere" }}>{watchVideo ? watchVideo.title : "Tutorial"}</strong>
          <IconButton onClick={() => setWatchVideo(null)} aria-label="Fechar vídeo"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 0 }}>
          {currentId && <div className={classes.player}><iframe className={classes.iframe} title={watchVideo.title || "Tutorial"} src={`https://www.youtube-nocookie.com/embed/${currentId}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
        </DialogContent>
      </Dialog>
      <ConfirmationModal title="Exclusão de Registro" open={showConfirmDialog} onClose={setShowConfirmDialog} onConfirm={handleDelete}>Deseja realmente excluir esse registro?</ConfirmationModal>
    </div>
  );
}
